import { Loader } from '@fountain/fountain-ui-components';
import React, { useCallback, useEffect, useRef, useState, VFC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';

import {
  BootstrapRunStatus,
  createFaqSection,
  createKnowledgeBase,
  deleteFaqSection,
  downloadFaq,
  getLatestBootstrapStatus,
  retrieveKnowledgeBase,
  triggerBootstrap,
  updateAxAgent,
  updateFaqSection,
  uploadFileSource,
} from 'containers/ChatAgent/api';
import { messages } from 'containers/ChatAgent/messages';
import {
  AxAgentValues,
  FaqEntry,
  KnowledgeBaseResponse,
} from 'containers/ChatAgent/types';
import { buildKnowledgeBaseExportFilename } from 'containers/ChatAgent/utils';
import { addMessageAction } from 'containers/FlashMessage/actions';

import { KBEditor, TopicContent } from './KBEditor';
import { KB_TOPICS, Sidebar } from './Sidebar';
import { UploadScreen } from './UploadScreen';
import { useAgentContextStyles } from './styles';

type AgentContextView = 'upload' | 'editor';

const POLL_INTERVAL_MS = 4000;
const POLL_MAX_TICKS = 150; // 150 × 4 s = 600 s ≈ 10 min

export type ProcessingState = 'idle' | 'processing' | 'completed' | 'failed';

export interface AgentContextProps {
  setAxAgentValues: React.Dispatch<React.SetStateAction<AxAgentValues>>;
  accessToken: string;
  axAgentValues: AxAgentValues;
  knowledgeBase?: KnowledgeBaseResponse;
  isKnowledgeBaseUploaded: boolean;
  setIsKnowledgeBaseUploaded: React.Dispatch<React.SetStateAction<boolean>>;
  onViewVersionHistory: () => void;
  showVersionHistory: boolean;
  restoreToken?: number;
  onFaqChange?: () => void;
}

export const AgentContext: VFC<AgentContextProps> = ({
  setAxAgentValues,
  accessToken,
  axAgentValues,
  knowledgeBase,
  isKnowledgeBaseUploaded,
  setIsKnowledgeBaseUploaded,
  onViewVersionHistory,
  showVersionHistory,
  restoreToken,
  onFaqChange,
}) => {
  const classes = useAgentContextStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { external_id: currentUserEmail } = useSelector(makeSelectWhoami());

  const hasExistingContent =
    isKnowledgeBaseUploaded ||
    !!axAgentValues.knowledgeBaseUuid ||
    (knowledgeBase && (knowledgeBase.uploads?.length ?? 0) > 0) ||
    (knowledgeBase && (knowledgeBase.faqs?.length ?? 0) > 0);

  const [view, setView] = useState<AgentContextView>(
    hasExistingContent ? 'editor' : 'upload',
  );
  const [activeTopicIndex, setActiveTopicIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // Optimistically lock the editor when an upload just completed (isKnowledgeBaseUploaded=true
  // on mount). The initial status check corrects this to 'idle' if bootstrap already finished.
  const [processingState, setProcessingState] = useState<ProcessingState>(
    isKnowledgeBaseUploaded ? 'processing' : 'idle',
  );

  const [topicContents, setTopicContents] = useState<TopicContent[]>(
    KB_TOPICS.map(t => ({ topic: t, content: '' })),
  );
  const faqUuidMap = useRef<Map<string, string>>(new Map());
  const creatingKbRef = useRef<Promise<string> | null>(null);
  // Ref so saveTopic always calls the latest onFaqChange without being in its dep array
  const onFaqChangeRef = useRef(onFaqChange);
  useEffect(() => {
    onFaqChangeRef.current = onFaqChange;
  });
  // Ref so handleRenameTopic can flush the latest typed-but-unsaved content
  // alongside the rename, without needing topicContents (which changes on
  // every keystroke) in its dependency array.
  const topicContentsRef = useRef(topicContents);
  useEffect(() => {
    topicContentsRef.current = topicContents;
  }, [topicContents]);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialKbUuidRef = useRef(axAgentValues.knowledgeBaseUuid);

  // Session counter — incrementing it invalidates any in-flight tick from
  // the previous polling session without needing to track timer IDs directly.
  const pollingSessionRef = useRef(0);

  const stopPolling = useCallback(() => {
    pollingSessionRef.current += 1;
    if (pollTimerRef.current !== null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const reloadFaqsFromApi = useCallback(async () => {
    const kbUuid = axAgentValues.knowledgeBaseUuid;
    if (!kbUuid) return;

    try {
      const kb = await retrieveKnowledgeBase(accessToken, kbUuid);
      const faqs: FaqEntry[] = kb.faqs ?? [];

      if (faqs.length > 0) {
        const newMap = new Map<string, string>();
        const updates: Record<string, string> = {};
        faqs.forEach(faq => {
          newMap.set(faq.question, faq.uuid);
          updates[faq.question] = faq.answer;
        });
        faqUuidMap.current = newMap;
        setTopicContents(prev => {
          const existingTopicNames = new Set(prev.map(t => t.topic));
          const customTopics = faqs
            .filter(faq => !existingTopicNames.has(faq.question))
            .map(faq => ({ topic: faq.question, content: faq.answer }));
          return [
            ...prev.map(t => ({
              ...t,
              content: updates[t.topic] ?? t.content,
            })),
            ...customTopics,
          ];
        });
      }
    } catch {
      // silently ignore
    }
  }, [accessToken, axAgentValues.knowledgeBaseUuid]);

  const handleBootstrapStatusUpdate = useCallback(
    (status: BootstrapRunStatus) => {
      if (status === 'completed' || status === 'ready') {
        stopPolling();
        setProcessingState('completed');
        void reloadFaqsFromApi();
      } else if (status === 'failed') {
        stopPolling();
        setProcessingState('failed');
        dispatch(
          addMessageAction(
            intl.formatMessage(messages.uploadFileFailed),
            'error',
          ),
        );
      }
    },
    [dispatch, intl, reloadFaqsFromApi, stopPolling],
  );

  // Stable ref so the restoreToken effect always calls the latest
  // reloadFaqsFromApi without needing it in the dep array. Same pattern
  // as statusHandlerRef — avoids a stale closure when knowledgeBaseUuid
  // and restoreToken change in the same render.
  const reloadFaqsRef = useRef(reloadFaqsFromApi);
  useEffect(() => {
    reloadFaqsRef.current = reloadFaqsFromApi;
  });

  // Reload FAQs whenever a restore completes (restoreToken increments)
  useEffect(() => {
    if (!restoreToken) return;
    void reloadFaqsRef.current();
  }, [restoreToken]);

  // Stable ref so polling ticks always call the latest handler without
  // needing to recreate startPolling when handler deps change.
  const statusHandlerRef = useRef(handleBootstrapStatusUpdate);
  useEffect(() => {
    statusHandlerRef.current = handleBootstrapStatusUpdate;
  });

  // TODO: replace polling with Pusher (private-kb-<kbUuid> channel) post-deployment.
  // Recursive setTimeout (not setInterval) — each tick only fires after the
  // previous request resolves, avoiding overlapping requests on slow networks.
  const startPolling = useCallback(
    (kbUuid: string) => {
      stopPolling();
      setProcessingState('processing');

      const session = pollingSessionRef.current;
      let ticks = 0;

      const tick = async () => {
        if (pollingSessionRef.current !== session) return;

        if (ticks >= POLL_MAX_TICKS) {
          stopPolling();
          setProcessingState('failed');
          dispatch(
            addMessageAction(
              intl.formatMessage(messages.uploadFileFailed),
              'error',
            ),
          );
          return;
        }
        ticks += 1;

        try {
          const { runStatus } = await getLatestBootstrapStatus(
            accessToken,
            kbUuid,
          );
          if (pollingSessionRef.current !== session) return;
          statusHandlerRef.current(runStatus);
          if (runStatus === 'in_progress') {
            pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
          }
        } catch {
          if (pollingSessionRef.current === session) {
            pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
          }
        }
      };

      pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    },
    [accessToken, dispatch, intl, stopPolling],
  );

  // On mount: if a bootstrap was already in progress, resume polling.
  // The `cancelled` flag ensures only one startPolling call fires even
  // when React StrictMode double-invokes this effect.
  useEffect(() => {
    const kbUuid = initialKbUuidRef.current;
    if (!kbUuid) return;

    let cancelled = false;

    const checkInitialStatus = async () => {
      try {
        const { runStatus } = await getLatestBootstrapStatus(
          accessToken,
          kbUuid,
        );
        if (cancelled) return;
        if (runStatus === 'in_progress') {
          startPolling(kbUuid);
        } else {
          setProcessingState('idle');
        }
      } catch {
        if (!cancelled) setProcessingState('idle');
      }
    };

    void checkInitialStatus();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const handleAddTopic = (name: string) => {
    setTopicContents(prev => {
      const newIndex = prev.length;
      setActiveTopicIndex(newIndex);
      return [...prev, { topic: name, content: '' }];
    });
  };

  const topicRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Populate topics from the KB prop (parent fetches and passes this in).
  // No HTTP call here — reloadFaqsFromApi handles post-bootstrap refresh.
  useEffect(() => {
    if (!axAgentValues.knowledgeBaseUuid || !knowledgeBase) return;
    const faqs: FaqEntry[] = knowledgeBase.faqs ?? [];
    if (faqs.length === 0) return;

    const newMap = new Map<string, string>();
    const updates: Record<string, string> = {};
    faqs.forEach(faq => {
      newMap.set(faq.question, faq.uuid);
      updates[faq.question] = faq.answer;
    });
    faqUuidMap.current = newMap;
    setTopicContents(prev => {
      const existingTopicNames = new Set(prev.map(t => t.topic));
      const customTopics = faqs
        .filter(faq => !existingTopicNames.has(faq.question))
        .map(faq => ({ topic: faq.question, content: faq.answer }));
      return [
        ...prev.map(t => ({
          ...t,
          content: updates[t.topic] ?? t.content,
        })),
        ...customTopics,
      ];
    });
    setView('editor');
  }, [axAgentValues.knowledgeBaseUuid, knowledgeBase]);

  useEffect(() => {
    if (view === 'editor' && topicRefs.current[activeTopicIndex]) {
      topicRefs.current[activeTopicIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [activeTopicIndex, view]);

  const completedTopics = new Set(
    topicContents.filter(t => t.content.trim().length > 0).map(t => t.topic),
  );

  // useCallback (not a plain function) so it has a stable identity that
  // saveTopic/handleRenameTopic/handleDeleteTopic can safely list as a
  // dependency instead of disabling exhaustive-deps.
  const ensureKnowledgeBase = useCallback(async (): Promise<string> => {
    if (axAgentValues.knowledgeBaseUuid) return axAgentValues.knowledgeBaseUuid;
    if (creatingKbRef.current) return creatingKbRef.current;
    const creation = (async () => {
      try {
        const kb = await createKnowledgeBase(accessToken, 'v1');
        setAxAgentValues(prev => ({ ...prev, knowledgeBaseUuid: kb.uuid }));
        await updateAxAgent(accessToken, axAgentValues.axAgentId, {
          knowledgeBaseUuid: kb.uuid,
        });
        return kb.uuid;
      } finally {
        creatingKbRef.current = null;
      }
    })();
    creatingKbRef.current = creation;
    return creation;
  }, [
    accessToken,
    axAgentValues.axAgentId,
    axAgentValues.knowledgeBaseUuid,
    setAxAgentValues,
  ]);

  const saveTopic = useCallback(
    async (topic: string, content: string): Promise<void> => {
      const kbUuid = await ensureKnowledgeBase();
      const existingUuid = faqUuidMap.current.get(topic);

      if (existingUuid) {
        await updateFaqSection(
          accessToken,
          kbUuid,
          existingUuid,
          { answer: content },
          currentUserEmail,
        );
      } else {
        const created = await createFaqSection(
          accessToken,
          kbUuid,
          topic,
          content,
          currentUserEmail,
        );
        faqUuidMap.current.set(topic, created.uuid);
      }
      onFaqChangeRef.current?.();
    },
    [accessToken, currentUserEmail, ensureKnowledgeBase],
  );

  const handleRenameTopic = useCallback(
    async (oldName: string, newName: string) => {
      const existingUuid = faqUuidMap.current.get(oldName);
      // Flush whatever's currently typed for this topic alongside the
      // rename — otherwise KBEditor's stale-timer cleanup (which cancels
      // the pending debounced save once oldName is renamed away) would
      // silently drop an in-flight, not-yet-saved content edit.
      const currentContent = topicContentsRef.current.find(
        t => t.topic === oldName,
      )?.content;

      setTopicContents(prev =>
        prev.map(t => (t.topic === oldName ? { ...t, topic: newName } : t)),
      );
      if (existingUuid) {
        faqUuidMap.current.delete(oldName);
        faqUuidMap.current.set(newName, existingUuid);
      }

      // Nothing persisted yet and nothing typed — pure rename of an empty,
      // never-saved topic. No backend call needed.
      if (!existingUuid && !currentContent) return;

      try {
        const kbUuid = await ensureKnowledgeBase();

        if (existingUuid) {
          await updateFaqSection(
            accessToken,
            kbUuid,
            existingUuid,
            currentContent === undefined
              ? { question: newName }
              : { question: newName, answer: currentContent },
            currentUserEmail,
          );
        } else {
          // Never persisted — the pending debounced create that would have
          // done this got cancelled by KBEditor's stale-timer cleanup
          // (triggered by the topic key changing on rename). Create it
          // under the new name now so the already-typed content isn't lost.
          const created = await createFaqSection(
            accessToken,
            kbUuid,
            newName,
            currentContent ?? '',
            currentUserEmail,
          );
          faqUuidMap.current.set(newName, created.uuid);
        }
        onFaqChangeRef.current?.();
      } catch {
        // Revert the optimistic rename on failure.
        setTopicContents(prev =>
          prev.map(t => (t.topic === newName ? { ...t, topic: oldName } : t)),
        );
        faqUuidMap.current.delete(newName);
        if (existingUuid) {
          faqUuidMap.current.set(oldName, existingUuid);
        }
        dispatch(
          addMessageAction(
            intl.formatMessage(messages.topicRenameFailed),
            'error',
          ),
        );
      }
    },
    [accessToken, currentUserEmail, dispatch, ensureKnowledgeBase, intl],
  );

  const handleDeleteTopic = useCallback(
    async (topic: string) => {
      const existingUuid = faqUuidMap.current.get(topic);

      const removeLocally = () => {
        setTopicContents(prev => {
          const removedIndex = prev.findIndex(t => t.topic === topic);
          const next = prev.filter(t => t.topic !== topic);
          if (removedIndex !== -1) {
            setActiveTopicIndex(current => {
              if (removedIndex < current) return current - 1;
              if (removedIndex === current) {
                return Math.max(0, Math.min(current, next.length - 1));
              }
              return current;
            });
          }
          return next;
        });
        faqUuidMap.current.delete(topic);
      };

      if (!existingUuid) {
        removeLocally();
        return;
      }

      try {
        const kbUuid = await ensureKnowledgeBase();
        await deleteFaqSection(
          accessToken,
          kbUuid,
          existingUuid,
          currentUserEmail,
        );
        removeLocally();
        onFaqChangeRef.current?.();
      } catch {
        dispatch(
          addMessageAction(
            intl.formatMessage(messages.topicDeleteFailed),
            'error',
          ),
        );
      }
    },
    [accessToken, currentUserEmail, dispatch, ensureKnowledgeBase, intl],
  );

  const handleTopicContentChange = useCallback(
    (topic: string, content: string) => {
      setTopicContents(prev =>
        prev.map(t => (t.topic === topic ? { ...t, content } : t)),
      );
    },
    [],
  );

  const handleUploadSuccess = (kbUuid: string) => {
    setIsKnowledgeBaseUploaded(true);
    setAxAgentValues(prev => ({ ...prev, knowledgeBaseUuid: kbUuid }));
    startPolling(kbUuid);
    setView('editor');
  };

  const handleSkip = async () => {
    try {
      setIsLoading(true);
      await ensureKnowledgeBase();
    } catch {
      // continue even if KB creation fails
    } finally {
      setIsLoading(false);
    }
    setView('editor');
  };

  const handleUploadContext = async (file: File) => {
    try {
      setIsLoading(true);
      const kbUuid = await ensureKnowledgeBase();
      await uploadFileSource(accessToken, kbUuid, file);
      // Await the trigger so the bootstrap record exists before the first poll tick.
      await triggerBootstrap(
        accessToken,
        kbUuid,
        file,
        axAgentValues.axAgentId,
      );
      setIsKnowledgeBaseUploaded(true);
      startPolling(kbUuid);
    } catch {
      stopPolling();
      setProcessingState('failed');
      dispatch(
        addMessageAction(
          intl.formatMessage(messages.uploadFileFailed),
          'error',
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (!axAgentValues.knowledgeBaseUuid) return;
    let url: string | undefined;
    try {
      const blob = await downloadFaq(
        accessToken,
        axAgentValues.knowledgeBaseUuid,
      );
      url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        buildKnowledgeBaseExportFilename(axAgentValues.chatbotName),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      dispatch(
        addMessageAction(
          intl.formatMessage(messages.downloadFileSuccess),
          'success',
        ),
      );
    } catch {
      dispatch(
        addMessageAction(
          intl.formatMessage(messages.downloadFileFailed),
          'error',
        ),
      );
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  };

  if (isLoading) return <Loader fullScreen size="2rem" />;

  const isProcessing = processingState === 'processing';

  return (
    <div className={classes.wrapper}>
      <div className={classes.root}>
        <Sidebar
          topics={topicContents.map(t => t.topic)}
          completedTopics={completedTopics}
          activeTopicIndex={activeTopicIndex}
          onTopicClick={setActiveTopicIndex}
          onAddTopic={handleAddTopic}
          onRenameTopic={(oldName, newName) =>
            void handleRenameTopic(oldName, newName)
          }
          onDeleteTopic={topic => void handleDeleteTopic(topic)}
          disabled={view === 'upload'}
        />

        {view === 'upload' && (
          <UploadScreen
            accessToken={accessToken}
            axAgentValues={axAgentValues}
            setAxAgentValues={setAxAgentValues}
            onSkip={() => void handleSkip()}
            onUploadSuccess={handleUploadSuccess}
          />
        )}

        {view === 'editor' && (
          <KBEditor
            topicContents={topicContents}
            onTopicContentChange={handleTopicContentChange}
            onSaveTopic={(topic, content) => void saveTopic(topic, content)}
            onUploadContext={file => void handleUploadContext(file)}
            onExport={() => void handleExport()}
            topicRefs={topicRefs}
            processingState={processingState}
            onDismissSuccessBanner={() => setProcessingState('idle')}
            onViewVersionHistory={onViewVersionHistory}
            showVersionHistory={showVersionHistory}
          />
        )}
      </div>
    </div>
  );
};
